import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Anchor,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { DEFAULT_SUBSCRIBER_ACCESS, type SubscriberAccess } from '@quizee/core';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import type { AuditChangeView, AuditEntry, OrgSettingsView } from '../api/types';
import { auditFieldLabel, auditNote, auditSentence, auditValueText } from '../audit';
import {
  ALL_TIME,
  RANGE_PRESETS,
  type DayRange,
  isDay,
  isFiltered,
  matchingPreset,
  paramsForRange,
  presetRange,
  rangeFromParams,
  rangeInstants,
  rangeWords,
} from '../audit-range';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { ReadEdit } from '../components/ReadEdit';
import { ReadList, ReadRow } from '../components/ReadValue';
import { roleLabel, whenLabel } from '../format';

/**
 * The two questions about who can see what: who may read your readers' personal data, and who has
 * changed this publisher's settings (D32, D35).
 *
 * They share a screen because they are the same subject from two sides. One is the rule, the other
 * is the record of the rule being changed, and somebody who has come here worried about either is
 * usually about to ask the other.
 *
 * The subscriber setting is shown to every member and changed only by an owner, because an editor
 * refused the addresses is entitled to know that a rule is why. The trail below it is owners only
 * at the API and is not offered to anybody else at all.
 *
 * It is read by somebody with a question, almost always "who changed this and when", so the actor,
 * what they did and what they did it to are one sentence at the top of each record and everything
 * else is smaller underneath. The before and after values are there for the moment the answer to
 * the first question is "yes, but what did it say before".
 *
 * The dates are a real filter, done by the route, which is why they are here at all: a filter
 * applied to the page already on screen would look like a search while silently missing everything
 * past the cursor. Only what the route can do is offered. Filtering by person or by action is not
 * in it, so neither is on this screen.
 *
 * The range lives in the address, as the days themselves rather than the name of a preset, so a
 * link sent to a colleague shows what the sender was looking at and a reload keeps it. There is no
 * page number in the address, because paging is a cursor and a cursor belongs to the range it was
 * issued for: changing the range starts the list again.
 *
 * The trail itself is owners only and nothing is asked of the server for anybody else: an editor
 * who is shown a list and then refused by the server has been told the same thing twice, badly.
 * The server checks as well, and it is the server's check that matters, this one is only manners.
 *
 * Everything on this screen is text. No value from a record is ever rendered as markup: consent
 * wording is stored as sanitised markup, and an audit value can hold anything somebody typed.
 *
 * Nothing here writes. The trail is append only, so there is no edit, no delete and no button that
 * looks like either.
 */
export function Security() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [params, setParams] = useSearchParams();
  const range = rangeFromParams(params);
  const filtered = isFiltered(range);
  const words = rangeWords(range);
  const { from, to } = rangeInstants(range);
  /**
   * Two dates the wrong way round, which is a period that cannot contain anything.
   *
   * Nothing is asked for. The route refuses this pair as well, and rightly, but an error card is
   * the wrong shape for a typo somebody can see and fix in the box above it.
   */
  const backwards = range.from !== null && range.to !== null && range.from > range.to;

  const [entries, setEntries] = useState<readonly AuditEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  /**
   * Which list is on screen. A request that started under one range must not land on another, so
   * every request carries the number the list had when it was sent and a late one is dropped.
   */
  const shown = useRef(0);

  const load = useCallback(() => {
    if (orgId === null || !isOwner) return;
    const mine = ++shown.current;
    setProblem(null);
    setEntries(null);
    setCursor(null);
    if (backwards) return;
    api
      .auditTrail(orgId, {
        ...(from === undefined ? {} : { from }),
        ...(to === undefined ? {} : { to }),
      })
      .then((page) => {
        if (shown.current !== mine) return;
        setEntries(page.entries);
        setCursor(page.next_cursor);
      })
      .catch((error: unknown) => {
        if (shown.current === mine) setProblem(errorMessage(error));
      });
  }, [backwards, from, isOwner, orgId, to]);

  useEffect(load, [load]);

  /**
   * The next page, which is the records older than the last one already shown, inside the same
   * range.
   *
   * The cursor comes back from the server and goes out again untouched, alongside the range it was
   * issued for. There is no page number here because there is no page number at the other end:
   * pages only go one way.
   */
  function showMore() {
    if (orgId === null || cursor === null) return;
    const mine = shown.current;
    setLoadingMore(true);
    api
      .auditTrail(orgId, {
        ...(from === undefined ? {} : { from }),
        ...(to === undefined ? {} : { to }),
        cursor,
      })
      .then((page) => {
        if (shown.current !== mine) return;
        setEntries((existing) => [...(existing ?? []), ...page.entries]);
        setCursor(page.next_cursor);
      })
      .catch((error: unknown) => {
        if (shown.current === mine) setProblem(errorMessage(error));
      })
      .finally(() => setLoadingMore(false));
  }

  /** A new range in the address, which restarts the list through the effect above. */
  function chooseRange(next: DayRange) {
    setParams(paramsForRange(next, params), { replace: true });
  }

  return (
    <>
      <Title order={1} size="h2">
        Security
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        Who inside {orgName} can see what your readers have handed over, and a record of every
        change anybody has made to these settings.
      </Text>

      <SubscriberAccessSection orgId={orgId} orgName={orgName} isOwner={isOwner} />

      <Divider my="xl" />

      <Title order={2} size="h3" mb="xs">
        Settings changes
      </Title>

      {!isOwner ? (
        <Text size="lg" mt="sm">
          Only an owner of {orgName} can see who has changed these settings. Ask one of them if you
          need to know what changed.
        </Text>
      ) : (
        <>
          <Text mb="lg">
            Who has changed {orgName}'s settings, and when: what they changed, and what it said
            before. Records are written as the change is saved and cannot be edited or removed by
            anybody.
          </Text>

          <RangeFilter range={range} onChange={chooseRange} backwards={backwards} />

          {!backwards && (
            <Text fw={500} mb="md">
              {words === null
                ? 'Every change ever recorded, newest first.'
                : `Changes ${words}, newest first.`}
            </Text>
          )}

          {problem !== null && <ErrorNote message={problem} onRetry={load} />}
          {problem === null && entries === null && !backwards && (
            <Loading label="Loading your settings changes" />
          )}

          {entries !== null && entries.length === 0 && (
            <EmptyTrail words={words} onClear={() => chooseRange(ALL_TIME)} />
          )}

          {entries !== null && entries.length > 0 && (
            <>
              <Stack component="ul" gap="xs" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {entries.map((entry) => (
                  <EntryCard key={entry.audit_id} entry={entry} />
                ))}
              </Stack>

              {cursor !== null && (
                <Group mt="md">
                  <Button variant="default" disabled={loadingMore} onClick={showMore}>
                    {loadingMore ? 'Loading...' : 'Show more'}
                  </Button>
                </Group>
              )}

              {filtered && (
                <Text size="sm" c="dimmed" mt="md">
                  This is the period above and nothing else. Changes made outside it are still
                  recorded.
                </Text>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

const ACCESS_LABELS: Readonly<Record<SubscriberAccess, string>> = {
  owners_only: 'Owners only',
  owners_and_members: 'Owners and members',
};

/**
 * What each answer actually means for the people in this publisher, said in what they can do
 * rather than in the names of the two settings.
 */
const ACCESS_NOTES: Readonly<Record<SubscriberAccess, string>> = {
  owners_only:
    'Everybody else sees how many readers have signed up and nothing about who they are. The list and the CSV are owners only.',
  owners_and_members:
    'Anybody you have added to this publisher can read every address you hold and export all of them. Right where the people who make the interactions are the people who run the newsletter.',
};

/**
 * Who may see and export the subscribers (D35).
 *
 * One setting for reading the list and for taking the CSV, because somebody who can read every
 * address on screen already has the list and an export button is not what gave it to them.
 *
 * Read first, like every other settings section: it opens saying what the answer is, and a form
 * appears only when an owner presses Edit. A member gets the same reading of it and no button,
 * which is the point of showing it to them at all: being refused the addresses is a decision
 * somebody made, and a screen that hid the decision would leave them thinking the product was
 * broken.
 *
 * It reads the publisher's settings itself rather than being handed them, because this section is
 * the only part of this screen that needs them and the trail beside it is loaded for owners only.
 */
function SubscriberAccessSection({
  orgId,
  orgName,
  isOwner,
}: {
  orgId: string | null;
  orgName: string;
  isOwner: boolean;
}) {
  const [view, setView] = useState<OrgSettingsView | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setLoadProblem(null);
    setView(null);
    api
      .orgSettings(orgId)
      .then(setView)
      .catch((error: unknown) => setLoadProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  /**
   * An absent answer is owners only, which is what a settings record written before this setting
   * existed holds. The API fills the default in as well; this is the same rule again on the screen
   * that would otherwise have to guess.
   */
  const access = view?.settings.subscriber_access ?? DEFAULT_SUBSCRIBER_ACCESS;

  async function save(next: SubscriberAccess): Promise<boolean> {
    if (orgId === null) return false;
    setProblem(null);
    try {
      setView(await api.saveOrgSettings(orgId, { subscriber_access: next }));
      notifications.show({ message: 'Saved.', color: 'violet' });
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  return (
    <>
      <Title order={2} size="h3" mb="xs">
        Subscriber email addresses
      </Title>
      <Text mb="md">
        Who inside {orgName} can see the email addresses readers have given you, on screen and as a
        CSV. It is one answer for both: somebody who can read every address already has the list,
        whether or not they are offered a download button.
      </Text>

      {loadProblem !== null && <ErrorNote message={loadProblem} onRetry={load} />}
      {loadProblem === null && view === null && <Loading label="Loading this setting" />}

      {view !== null && (
        <>
          {problem !== null && <ErrorNote message={problem} />}

          <ReadEdit<SubscriberAccess>
            noun="who can see subscribers"
            canEdit={isOwner}
            open={() => access}
            save={save}
            form={(draft, set) => <AccessChoice value={draft} onChange={set} />}
          >
            <ReadList>
              <ReadRow
                label="Who can see them"
                value={ACCESS_LABELS[access]}
                note={ACCESS_NOTES[access]}
              />
            </ReadList>
          </ReadEdit>

          {/*
           * The door this setting does not close.
           *
           * Tightening it is exactly the moment somebody assumes every way to the addresses is
           * now shut, and a key is not governed by it: an owner mints one, it carries its own
           * access, and whatever holds it goes on reading subscribers server to server whichever
           * answer is chosen above. Said here, next to the choice, with a way to go and look.
           */}
          <Text size="sm" mt="md">
            This does not cover API keys. A key is made by an owner and carries its own access, so
            anything holding one can still read {orgName}'s subscribers whichever answer is set
            here. To close that door, revoke the key:{' '}
            <Anchor component={Link} to="/settings/developers" size="sm">
              see the keys this publisher has
            </Anchor>
            .
          </Text>

          {!isOwner && (
            <Text c="dimmed" size="sm" mt="sm">
              Only an owner can change this, so ask one of the owners of {orgName} if you need the
              addresses.
            </Text>
          )}
        </>
      )}
    </>
  );
}

/** The two answers, described by what each one lets people do. */
function AccessChoice({
  value,
  onChange,
}: {
  value: SubscriberAccess;
  onChange: (value: SubscriberAccess) => void;
}) {
  return (
    <Radio.Group
      value={value}
      onChange={(next) =>
        onChange(next === 'owners_and_members' ? 'owners_and_members' : 'owners_only')
      }
      label="Who can see and export subscriber email addresses"
      description="Counts stay visible to everybody either way, because a number is not personal data."
    >
      <Stack gap="sm" mt="xs">
        <Radio
          id="subscriber-access-owners"
          value="owners_only"
          label={ACCESS_LABELS.owners_only}
          description={ACCESS_NOTES.owners_only}
        />
        <Radio
          id="subscriber-access-members"
          value="owners_and_members"
          label={ACCESS_LABELS.owners_and_members}
          description={ACCESS_NOTES.owners_and_members}
        />
      </Stack>
    </Radio.Group>
  );
}

/**
 * The period being read.
 *
 * Three presets, because "the last week" and "the last month" are most of what anybody asks, and
 * two date boxes for the time somebody is looking for a particular afternoon. Both ends are whole
 * days as the person reading sees them: the last day is included in full, which is worth saying
 * next to the box rather than leaving somebody to find out by missing a change.
 */
function RangeFilter({
  range,
  onChange,
  backwards,
}: {
  range: DayRange;
  onChange: (range: DayRange) => void;
  backwards: boolean;
}) {
  const selected = matchingPreset(range);

  /** A typed or picked day. An empty box is that end left open, not a filter on nothing. */
  function setEnd(end: 'from' | 'to', value: string) {
    if (value !== '' && !isDay(value)) return;
    onChange({ ...range, [end]: value === '' ? null : value });
  }

  return (
    <Card withBorder radius="lg" p="md" component="section" mb="md">
      <Group gap="md" align="flex-end" wrap="wrap">
        <Group gap="xs" role="group" aria-label="Quick ranges">
          {RANGE_PRESETS.map(({ preset, label }) => (
            <Button
              key={preset}
              size="sm"
              variant={selected === preset ? 'filled' : 'default'}
              aria-pressed={selected === preset}
              onClick={() => onChange(presetRange(preset))}
            >
              {label}
            </Button>
          ))}
        </Group>

        <TextInput
          id="audit-from"
          type="date"
          label="From"
          value={range.from ?? ''}
          onChange={(event) => setEnd('from', event.currentTarget.value)}
        />
        <TextInput
          id="audit-to"
          type="date"
          label="To"
          description="Includes the whole of that day."
          value={range.to ?? ''}
          onChange={(event) => setEnd('to', event.currentTarget.value)}
        />
      </Group>

      {backwards && (
        <Text size="sm" mt="sm">
          The From date is after the To date, so nothing can fall between them. Swap them to see
          what changed. Nothing has been read while they are this way round, so this is not an
          answer about your trail.
        </Text>
      )}
    </Card>
  );
}

/**
 * Nothing to show, which is two different things.
 *
 * A publisher who has never changed anything and a publisher who changed nothing last week need
 * different sentences: the first is normal for a new account, the second is an answer to the
 * question that was asked, and reading one as the other would be reading a filtered view as the
 * whole trail.
 */
function EmptyTrail({ words, onClear }: { words: string | null; onClear: () => void }) {
  if (words !== null) {
    return (
      <Card withBorder radius="md" p="lg">
        <Text fw={500}>No changes in this period</Text>
        <Text size="sm" c="dimmed" mt={4}>
          Nothing was changed {words}. Changes made outside those dates are still here, under a
          wider range.
        </Text>
        <Group mt="md">
          <Button variant="default" size="sm" onClick={onClear}>
            Show all time
          </Button>
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="lg">
      <Text fw={500}>Nothing has been changed yet</Text>
      <Text size="sm" c="dimmed" mt={4}>
        This is normal for a new publisher. As soon as somebody edits a brand, the consent notice,
        the team or a key, it will show up here.
      </Text>
    </Card>
  );
}

/**
 * One record: the sentence, then who and when, then what moved.
 *
 * The sentence carries the actor's name rather than their id because the name is what somebody
 * recognises, and the record keeps the name it had at the time, so this still reads after they
 * have left. The address is underneath for the case where two people share a first name.
 */
function EntryCard({ entry }: { entry: AuditEntry }) {
  const note = auditNote(entry);
  const role = entry.actor.role === null ? null : roleLabel(entry.actor.role);

  return (
    <Card component="li" withBorder radius="md" p="md">
      <Text fw={500}>
        {entry.actor.name} {auditSentence(entry)}
      </Text>

      <Group gap="xs" mt={2}>
        <Text size="sm" c="dimmed">
          {whenLabel(entry.occurred_at)}
        </Text>
        {entry.actor.email !== null && (
          <>
            <Text size="sm" c="dimmed" aria-hidden="true">
              &middot;
            </Text>
            <Text size="sm" c="dimmed">
              {entry.actor.email}
            </Text>
          </>
        )}
        {role !== null && (
          <>
            <Text size="sm" c="dimmed" aria-hidden="true">
              &middot;
            </Text>
            <Text size="sm" c="dimmed">
              {role}
            </Text>
          </>
        )}
      </Group>

      {note !== null && (
        <Text size="sm" c="dimmed" mt="xs">
          {note}
        </Text>
      )}

      {entry.changes.length > 0 && (
        <Stack gap="sm" mt="sm">
          {entry.changes.map((change) => (
            <ChangeRow key={change.field} change={change} />
          ))}
        </Stack>
      )}
    </Card>
  );
}

/** One field, with what it held before and what it holds now. */
function ChangeRow({ change }: { change: AuditChangeView }) {
  return (
    <Box>
      <Text size="sm" fw={500}>
        {auditFieldLabel(change.field)}
      </Text>
      <ValueLine label="Before" value={change.before} truncated={change.before_truncated} />
      <ValueLine label="After" value={change.after} truncated={change.after_truncated} />
    </Box>
  );
}

/**
 * One side of a change.
 *
 * A value that was cut says so in its own line rather than being dressed up as the whole thing:
 * somebody comparing two versions of a consent notice needs to know they are looking at the start
 * of one. `fs="italic"` is only for "nothing set", so an absent value never reads as typed text.
 */
function ValueLine({
  label,
  value,
  truncated,
}: {
  label: string;
  value: unknown;
  truncated: boolean;
}) {
  const shown = auditValueText(value, truncated);

  return (
    <Group gap="xs" align="flex-start" wrap="nowrap" mt={2}>
      <Text size="sm" c="dimmed" style={{ flex: '0 0 3.5rem' }}>
        {label}
      </Text>
      <Box style={{ minWidth: 0 }}>
        <Text
          size="sm"
          {...(shown.empty ? { c: 'dimmed' as const, fs: 'italic' as const } : {})}
          style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {shown.text}
        </Text>
        {truncated && (
          <Text size="xs" c="dimmed">
            Shortened when it was recorded. This is the start of the value, not all of it.
          </Text>
        )}
      </Box>
    </Group>
  );
}
